(function () {
  if (!window.customElements || customElements.get('fa26-carousel')) return;

  const reducedMotionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function toNumber(value) {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  function closestIndex(positions, target) {
    let best = 0;
    let bestDistance = Infinity;
    positions.forEach((position, i) => {
      const distance = Math.abs(position - target);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    return best;
  }

  class Fa26Carousel extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.index = -1;
      this.total = 0;
      this.pages = 1;
      this.track = null;
      this.frameRequested = false;
      this.handleScroll = this.handleScroll.bind(this);
      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleDashClick = this.handleDashClick.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleMutation = this.handleMutation.bind(this);
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.destroy();
      this.track = this.querySelector('[data-fa26-track]');
      if (!this.track) return;
      this.progress = this.querySelector('[data-fa26-progress]');
      this.thumb = this.progress ? this.progress.querySelector('.fa26-progress__thumb') : null;
      this.counter = this.querySelector('[data-fa26-counter]');
      this.dashes = this.querySelector('[data-fa26-dashes]');
      this.prevButton = this.querySelector('[data-fa26-prev]');
      this.nextButton = this.querySelector('[data-fa26-next]');
      this.counterFormat = this.getAttribute('data-counter-format') || '{current} of {total}';
      this.dashLabel = this.getAttribute('data-dash-label') || 'Ir al elemento {index}';
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      if (this.prevButton) this.prevButton.addEventListener('click', this.handlePrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.handleNext);
      if (this.dashes) this.dashes.addEventListener('click', this.handleDashClick);
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.handleResize);
      }
      if (window.MutationObserver) {
        this.mutationObserver = new MutationObserver(this.handleMutation);
        this.mutationObserver.observe(this.track, { childList: true, attributes: true, attributeFilter: ['hidden'], subtree: true });
      }
      this.refresh();
    }

    destroy() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.handleScroll);
      if (this.prevButton) this.prevButton.removeEventListener('click', this.handlePrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.handleNext);
      if (this.dashes) this.dashes.removeEventListener('click', this.handleDashClick);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      window.removeEventListener('resize', this.handleResize);
      this.resizeObserver = null;
      this.mutationObserver = null;
      this.track = null;
    }

    get centered() {
      const item = this.items[0];
      if (item) return (getComputedStyle(item).scrollSnapAlign || '').includes('center');
      return this.classList.contains('fa26-carousel--center');
    }

    get scrollBehavior() {
      return reducedMotionQuery && reducedMotionQuery.matches ? 'auto' : 'smooth';
    }

    refresh() {
      if (!this.track) return;
      this.items = Array.prototype.filter.call(this.track.children, (child) => !child.hidden);
      this.total = this.items.length;
      this.renderDashes();
      this.update();
    }

    measure() {
      const track = this.track;
      const style = getComputedStyle(track);
      const origin = track.getBoundingClientRect().left + track.clientLeft - track.scrollLeft;
      const gap = toNumber(style.columnGap) || toNumber(style.gap);
      const paddingStart = toNumber(style.paddingLeft);
      const paddingEnd = toNumber(style.paddingRight);
      const scrollPadding = toNumber(style.scrollPaddingLeft) || toNumber(style.scrollPaddingInlineStart);
      const lefts = this.items.map((item) => item.getBoundingClientRect().left - origin);
      const widths = this.items.map((item) => item.offsetWidth);
      const itemWidth = widths[0] || track.clientWidth;
      const visibleCount = Math.max(1, Math.floor((track.clientWidth - paddingStart - paddingEnd + gap) / (itemWidth + gap)));
      return { gap, scrollPadding, lefts, widths, visibleCount };
    }

    update() {
      if (!this.track) return;
      const track = this.track;
      const metrics = this.measure();
      this.pages = Math.max(1, this.total - metrics.visibleCount + 1);
      const target = this.centered ? track.scrollLeft + track.clientWidth / 2 : track.scrollLeft + metrics.scrollPadding;
      const positions = this.centered ? metrics.lefts.map((left, i) => left + metrics.widths[i] / 2) : metrics.lefts;
      const index = this.total ? Math.min(closestIndex(positions, target), this.pages - 1) : 0;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      const atStart = track.scrollLeft <= 1;
      this.classList.toggle('has-more-before', !atStart);
      this.classList.toggle('has-more-after', !atEnd);
      this.classList.toggle('is-static', track.scrollWidth <= track.clientWidth + 1);
      this.renderControls(index, atEnd);
      if (index !== this.index) {
        this.index = index;
        this.dispatchEvent(new CustomEvent('fa26:change', { bubbles: true, detail: { index: index, total: this.total } }));
      }
    }

    renderControls(index, atEnd) {
      const current = this.total ? Math.min(index + 1, this.total) : 0;
      if (this.prevButton) this.prevButton.disabled = index <= 0;
      if (this.nextButton) this.nextButton.disabled = index >= this.pages - 1 || atEnd;
      if (this.thumb) {
        this.thumb.style.width = 100 / this.pages + '%';
        this.thumb.style.transform = 'translateX(' + index * 100 + '%)';
      }
      if (this.counter) {
        this.counter.textContent = this.counterFormat.split('{current}').join(current).split('{total}').join(this.total);
      }
      if (this.dashes) {
        Array.prototype.forEach.call(this.dashes.children, (dash, i) => {
          dash.classList.toggle('is-active', i === index);
          if (i === index) dash.setAttribute('aria-current', 'true');
          else dash.removeAttribute('aria-current');
        });
      }
    }

    renderDashes() {
      if (!this.dashes) return;
      this.dashes.textContent = '';
      this.items.forEach((item, i) => {
        const dash = document.createElement('button');
        dash.type = 'button';
        dash.className = 'fa26-dashes__item';
        dash.setAttribute('aria-label', this.dashLabel.split('{index}').join(i + 1));
        this.dashes.appendChild(dash);
      });
    }

    scrollToIndex(requested) {
      if (!this.track || !this.total) return;
      const i = Math.max(0, Math.min(this.total - 1, requested));
      const metrics = this.measure();
      const left = this.centered
        ? metrics.lefts[i] + metrics.widths[i] / 2 - this.track.clientWidth / 2
        : metrics.lefts[i] - metrics.scrollPadding;
      this.track.scrollTo({ left: Math.max(0, left), behavior: this.scrollBehavior });
    }

    handlePrev() {
      this.scrollToIndex(this.index - 1);
    }

    handleNext() {
      this.scrollToIndex(this.index + 1);
    }

    handleDashClick(event) {
      const dash = event.target.closest('.fa26-dashes__item');
      if (!dash || dash.parentNode !== this.dashes) return;
      this.scrollToIndex(Array.prototype.indexOf.call(this.dashes.children, dash));
    }

    handleScroll() {
      if (this.frameRequested) return;
      this.frameRequested = true;
      requestAnimationFrame(() => {
        this.frameRequested = false;
        this.update();
      });
    }

    handleResize() {
      this.update();
    }

    handleMutation() {
      this.refresh();
    }
  }

  window.Fa26Carousel = Fa26Carousel;
  customElements.define('fa26-carousel', Fa26Carousel);
})();
